import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { authFileClick } from "@/lib/openFile";
import ClientShell from "@/components/ClientShell";
import { ArrowLeft, FileText, DownloadSimple, Truck, Receipt, Certificate as CertIcon, ShieldCheck } from "@phosphor-icons/react";
import { formatDate, formatCurrency, formatDateTime } from "@/lib/format";
import StatusChip from "@/components/StatusChip";

// Shared page for DO (delivery-orders), Invoices, Certificates, Warranty
// Uses `orders` + attached files (kind='test_certificate','warranty','photo')
const CONFIGS = {
  "delivery-orders": {
    title: "Delivery Orders",
    tag: "DO",
    icon: Truck,
    description: "Delivery notes for orders that have shipped.",
    filter: (o) => ["SHIPPED", "DELIVERED"].includes(o.current_status),
    empty: "No orders have been shipped yet.",
  },
  invoices: {
    title: "Invoices",
    tag: "Invoices",
    icon: Receipt,
    description: "Line-item pricing for each of your orders.",
    filter: () => true,
    empty: "No invoices yet.",
  },
  certificates: {
    title: "Certificates",
    tag: "Certificates",
    icon: CertIcon,
    description: "Quality and test certificates attached to your orders.",
    filter: () => true,
    empty: "No certificates uploaded yet.",
    fileKinds: ["test_certificate"],
  },
  warranty: {
    title: "Warranty",
    tag: "Warranty",
    icon: ShieldCheck,
    description: "Warranty details and warranty documents.",
    filter: () => true,
    empty: "No warranty information yet.",
    fileKinds: ["warranty"],
  },
};

export default function Documents() {
  const { kind } = useParams();
  const config = CONFIGS[kind] || CONFIGS.invoices;
  const Icon = config.icon;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders");
      setOrders(res.data);
    } catch (e) {
      console.error("[Documents] failed:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const list = useMemo(() => orders.filter(config.filter), [orders, config]);

  return (
    <ClientShell>
      <Link to="/more" data-testid="documents-back" className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6">
        <ArrowLeft size={14} /> Back to More
      </Link>

      <div className="mb-8 flex items-center gap-4">
        <div className="w-14 h-14 bg-[#0047AB]/10 text-[#0047AB] grid place-items-center">
          <Icon size={28} weight="duotone" />
        </div>
        <div>
          <div className="mt-tiny text-[#FF5A1F]">{config.tag}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-1">
            {config.title}
          </h1>
          <p className="mt-1 text-sm text-zinc-600">{config.description}</p>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3">{["s-1", "s-2"].map((k) => <div key={k} className="h-32 bg-white border border-zinc-200 animate-pulse" />)}</div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-14 text-center text-zinc-500">{config.empty}</div>
      ) : (
        <div className="grid gap-4" data-testid="documents-list">
          {list.map((o) => (
            <div key={o.order_id} className="bg-white border border-zinc-200 p-5 md:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="mt-tiny text-zinc-400 font-mono-mt">{o.order_number}</div>
                  <div className="font-display font-bold text-lg text-zinc-900 mt-1">{o.product_name}</div>
                </div>
                <StatusChip status={o.current_status} />
              </div>

              {kind === "invoices" && (
                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetaRow label="Qty" value={o.quantity} />
                  <MetaRow label="Unit" value={formatCurrency(o.unit_price)} />
                  <MetaRow label="Total" value={formatCurrency(o.total_price)} accent />
                  <MetaRow label="Ordered" value={formatDate(o.created_at)} />
                </div>
              )}

              {kind === "delivery-orders" && (
                <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                  <MetaRow label="Expected" value={formatDate(o.expected_delivery)} />
                  <MetaRow label="Client" value={o.client_name} />
                  <MetaRow label="Qty" value={o.quantity} />
                </div>
              )}

              {kind === "warranty" && (
                <div className="mt-5 text-sm text-zinc-700 leading-relaxed">
                  {o.warranty_info || <span className="text-zinc-400">Standard warranty applies</span>}
                  {o.manufacturing_date && (
                    <div className="mt-2 mt-tiny text-zinc-500">Warranty starts {formatDate(o.manufacturing_date)}</div>
                  )}
                </div>
              )}

              {config.fileKinds && (
                <div className="mt-5">
                  {(o.files || []).filter((f) => config.fileKinds.includes(f.kind)).length === 0 ? (
                    <div className="mt-tiny text-zinc-400">No {config.tag.toLowerCase()} uploaded for this order.</div>
                  ) : (
                    <div className="grid gap-2">
                      {(o.files || []).filter((f) => config.fileKinds.includes(f.kind)).map((f) => (
                        <a
                          key={f.file_id}
                          href={`${API}/files/${f.storage_path}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={authFileClick(f.storage_path)}
                          data-testid={`file-${f.file_id}`}
                          className="flex items-center justify-between border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 p-3 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText size={18} className="text-[#0047AB] shrink-0" weight="duotone" />
                            <div className="min-w-0 text-sm text-zinc-900 truncate">{f.original_filename}</div>
                          </div>
                          <DownloadSimple size={18} className="text-zinc-400" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-zinc-100">
                <Link to={`/orders/${o.order_id}`} data-testid={`view-order-${o.order_number}`} className="text-sm text-[#0047AB] hover:underline">
                  View full order →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </ClientShell>
  );
}

function MetaRow({ label, value, accent }) {
  return (
    <div className={accent ? "bg-zinc-900 text-white p-3" : ""}>
      <div className={`mt-tiny ${accent ? "text-white/60" : "text-zinc-400"}`}>{label}</div>
      <div className={`mt-1 font-medium ${accent ? "text-white" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}
